/**
 * Parse GoodGuys criteria JSON (array, or { results, result_node }, legacy wrappers).
 * Eligibility uses only `result_node` — no question/short-answer logic.
 */

export function unwrapCriteriaPayload(raw) {
  if (raw == null) return { sections: null, resultNode: null };
  let parsed = raw;
  if (typeof parsed === 'string') {
    try {
      parsed = JSON.parse(parsed);
    } catch {
      return { sections: null, resultNode: null };
    }
  }
  if (parsed?.result?.json != null) {
    parsed = parsed.result.json;
  } else if (parsed?.json != null && typeof parsed.json === 'object') {
    parsed = parsed.json;
  }
  if (Array.isArray(parsed)) {
    return { sections: parsed, resultNode: null };
  }
  if (parsed && typeof parsed === 'object') {
    const resultNode =
      typeof parsed.result_node === 'string'
        ? parsed.result_node
        : typeof parsed.resultNode === 'string'
          ? parsed.resultNode
          : null;
    if (Array.isArray(parsed.results)) {
      return { sections: parsed.results, resultNode };
    }
    if (Array.isArray(parsed.criteria)) {
      return { sections: parsed.criteria, resultNode };
    }
    if (Array.isArray(parsed.sections)) {
      return { sections: parsed.sections, resultNode };
    }
    if (Array.isArray(parsed.data)) {
      return { sections: parsed.data, resultNode };
    }
    if (resultNode) {
      return { sections: null, resultNode };
    }
  }
  return { sections: null, resultNode: null };
}

function eligibilityFromResultNode(resultNode) {
  const n = String(resultNode).trim().toLowerCase();
  if (!n) {
    return { eligible: null, notEligibleReasons: [], manualReviewRequired: true };
  }

  if (n === 'positive claim') {
    return { eligible: true, notEligibleReasons: [], manualReviewRequired: false };
  }
  if (n === 'no claim') {
    return { eligible: false, notEligibleReasons: [], manualReviewRequired: false };
  }
  if (
    n === 'manual check nessesary' ||
    n === 'manual check necessary' ||
    n.startsWith('manual check ')
  ) {
    return { eligible: null, notEligibleReasons: [], manualReviewRequired: true };
  }

  if (n === 'positive' || (n.includes('positive') && n.includes('claim'))) {
    return { eligible: true, notEligibleReasons: [], manualReviewRequired: false };
  }
  if (
    n === 'negative claim' ||
    n === 'negative' ||
    n === 'not eligible' ||
    (n.includes('negative') && n.includes('claim'))
  ) {
    return { eligible: false, notEligibleReasons: [], manualReviewRequired: false };
  }
  if (n.includes('manual') || n === 'review required' || n.includes('inconclusive')) {
    return { eligible: null, notEligibleReasons: [], manualReviewRequired: true };
  }
  return { eligible: null, notEligibleReasons: [], manualReviewRequired: true };
}

/** Eligibility from stored job result — only `result_node`. */
export function getEligibilityFromCriteriaResult(resultJson) {
  const { resultNode } = unwrapCriteriaPayload(resultJson);
  if (resultNode != null && String(resultNode).trim() !== '') {
    return eligibilityFromResultNode(resultNode);
  }
  return { eligible: null, notEligibleReasons: [], manualReviewRequired: true };
}

/** Sections array only (for UI). Null if not an array. */
export function normalizeCriteriaSections(resultJson) {
  const { sections } = unwrapCriteriaPayload(resultJson);
  return sections && Array.isArray(sections) ? sections : null;
}
